import {
  CiProductOptionValuesInput,
  GetBolttechCarInsurancePremiumsOperation,
  GetBolttechCarInsuranceQuotesOperation,
  GetBolttechVehicleMakersOperation,
  GetBolttechVehiclesOperation,
  GetBolttechVehicleTrimsOperation,
  GetCarInsurancePremiumsOperation,
  GetCarInsuranceProductsOperation,
  GetCarInsuranceQuotesOperation,
  GetVehicleMakersOperation,
  GetVehiclesOperation,
} from '__generated__/graphql';
import {
  createLazyQuery,
  EmptyQueryArgs,
} from 'car-insurance/hooks/useLazyQuery';
import {
  Brand,
  CarInsuranceProduct,
  ProductPremium,
  VehicleModel,
  VehicleTrim,
} from 'car-insurance/types';
// ToDo: should be removed after refactor
import { BrandId } from 'utils/override';
import { CmsServiceClientVehiclesDomainService } from '$/app/(cms)/_modules/cmsService/carInsurance/cmsServiceClientVehiclesDomainService';

export interface IVehicleMaker {
  id: string;
  name: string;
}

export interface IVehicleDomainService {
  getVehicleMakes({
    useBolttechData,
  }: {
    useBolttechData: boolean;
  }): Promise<IVehicleMaker[] | string[]>;

  getVehiclesData({
    makeName,
  }: {
    makeName: string;
    useBolttechData: boolean;
  }): Promise<VehicleModel[]>;

  /**
   * Get available manufacturing years for a specific vehicle model
   * @param brandName Brand name (aka make)
   * @param modelName Model name
   * @returns Array of manufacturing years (e.g., [2020, 2021, 2022])
   */
  getModelYears(brandName: string, modelName: string): Promise<number[]>;

  getVehicleTrims({
    makeId,
    modelId,
    year,
  }: {
    makeId: string;
    modelId: string;
    year: number | null;
  }): Promise<VehicleTrim[]>;
}

/**
 * Represents the Fair Market Value (FMV) of a vehicle.
 */
export interface IFairMarketValue {
  carId: number;
  fmv: number;
}

/**
 * Domain service for managing car insurance product operations.
 * Handles fetching insurance quotes, product details, and premium calculations
 * with support for both standard and Bolttech data sources.
 */
export class ClientProductsDomainService {
  public async getCompareQuote(
    params: CiProductOptionValuesInput,
    useBolttechData: boolean,
  ): Promise<string> {
    if (useBolttechData) {
      try {
        const query = createLazyQuery(GetBolttechCarInsuranceQuotesOperation);
        const response = await query({ request: params });

        const requestId = response?.bolttechCarInsuranceCompareQuote
          .clientRequestId as string;
        return requestId;
      } catch (error) {
        throw new Error('Failed to fetch compare quote');
      }
    }

    try {
      const query = createLazyQuery(GetCarInsuranceQuotesOperation);
      const response = await query({ request: params });
      const requestId = response?.carInsuranceCompareQuote
        .clientRequestId as string;
      return requestId;
    } catch (error) {
      throw new Error('Failed to fetch compare quote');
    }
  }
  public async getProducts(
    productsIds: string[],
    brand: string,
    lang: string,
  ): Promise<CarInsuranceProduct[]> {
    try {
      const query = createLazyQuery(GetCarInsuranceProductsOperation);
      const response = await query({
        fallback: true,
        locale: (brand as Brand) === 'singsaver' ? 'en' : lang,
        limit: null,
        skip: 0,
        where: {
          uidIn: productsIds,
        },
      });
      return response?.getAllProducts as unknown as CarInsuranceProduct[];
    } catch (error) {
      throw new Error('Failed to fetch products');
    }
  }

  public async getPremiums({
    requestId,
    useBolttechData,
  }: {
    requestId: string;
    useBolttechData: boolean;
  }): Promise<
    | {
        completed: boolean;
        premiums: ProductPremium[];
      }
    | ProductPremium[]
  > {
    if (useBolttechData) {
      try {
        const query = createLazyQuery(GetBolttechCarInsurancePremiumsOperation);
        const response = await query({ requestId });
        return response?.bolttechCarInsuranceGetPremiums as unknown as {
          completed: boolean;
          premiums: ProductPremium[];
        };
      } catch (error) {
        throw new Error('Failed to fetch premiums');
      }
    }

    try {
      const query = createLazyQuery(GetCarInsurancePremiumsOperation);
      const response = await query({ requestId });
      return response?.carInsuranceGetPremiums as unknown as ProductPremium[];
    } catch (error) {
      throw new Error('Failed to fetch premiums');
    }
  }

  /**
   * Unused method to comply with the IProductDomainService interface.
   * @returns A promise that resolves to a default fair market value object.
   */
  public getVehicleFairMarketValue(): Promise<IFairMarketValue> {
    return Promise.resolve({ carId: 0, fmv: 0 });
  }
}

/**
 * Domain service for managing vehicle-related data operations.
 * Provides access to vehicle makers, models, and trim variants
 * with support for both standard and Bolttech data sources.
 */
export class ClientVehiclesDomainService implements IVehicleDomainService {
  public async getVehiclesData({
    makeName,
    useBolttechData = false,
  }: {
    makeName: string;
    useBolttechData: boolean;
  }): Promise<VehicleModel[]> {
    if (useBolttechData) {
      try {
        const query = createLazyQuery(GetBolttechVehiclesOperation);
        const response = await query({
          makeId: makeName,
        });
        return response?.bolttechVehicleModels as VehicleModel[];
      } catch (error) {
        throw new Error('Failed to fetch vehicle');
      }
    }

    try {
      const query = createLazyQuery(GetVehiclesOperation);
      const response = await query({
        baseModel: null,
        carrierCode: null,
        manufactureYear: null,
        modelName: null,
        trim: null,
        makeName: makeName,
      });
      return response?.vehicles as VehicleModel[];
    } catch (error) {
      throw new Error('Failed to fetch vehicle');
    }
  }

  public async getVehicleTrims({
    makeId,
    modelId,
  }: {
    makeId: string;
    modelId: string;
    year: number | null;
  }): Promise<VehicleTrim[]> {
    try {
      const query = createLazyQuery(GetBolttechVehicleTrimsOperation);
      const response = await query({
        makeId,
        modelId,
      });
      return response?.bolttechVehicleVariants as VehicleTrim[];
    } catch (error) {
      throw new Error('Failed to fetch vehicle variants');
    }
  }

  public async getVehicleMakes({
    useBolttechData,
  }: {
    useBolttechData: boolean;
  }) {
    if (useBolttechData) {
      try {
        const query = createLazyQuery(GetBolttechVehicleMakersOperation);
        const response = await query(EmptyQueryArgs);

        return (
          (response?.bolttechVehicleMakers.filter(
            Boolean,
          ) as IVehicleMaker[]) ?? []
        );
      } catch (error) {
        throw new Error('Failed to fetch Bolttech vehicle makers');
      }
    }

    try {
      const query = createLazyQuery(GetVehicleMakersOperation);
      const response = await query(EmptyQueryArgs);

      return (response?.vehicleMakers.filter(Boolean) as string[]) ?? [];
    } catch (error) {
      throw new Error('Failed to fetch vehicle makers');
    }
  }

  public getModelYears(_: string, __: string): Promise<number[]> {
    return Promise.resolve([]);
  }
}

export const clientVehiclesDomainService = new ClientVehiclesDomainService();
export const clientProductsDomainService = new ClientProductsDomainService();

let cmsServiceClientVehiclesDomainService: CmsServiceClientVehiclesDomainService | null =
  null;

export function getVehicleDomainService(brand: BrandId): IVehicleDomainService {
  switch (brand) {
    case 'moneymax':
      if (!cmsServiceClientVehiclesDomainService) {
        cmsServiceClientVehiclesDomainService =
          new CmsServiceClientVehiclesDomainService();
      }

      return cmsServiceClientVehiclesDomainService;
    default:
      return clientVehiclesDomainService;
  }
}
